export interface SummaryScrollMetrics {
  scrollTop: number
  scrollHeight: number
  clientHeight: number
}

export interface SummaryCollapseStyle {
  /** Vertical scale of the summary card: 1 — open, 0 — flattened away. */
  scale: number
  /** Opacity of the card content; fades out by the middle of the collapse. */
  contentOpacity: number
  /** Downward shift during the top rubber-band (the card moves, never stretches). */
  bounceShift: number
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

/**
 * Hidden part of the summary in px: follows scrollTop 1:1, capped at summaryHeight.
 * Negative on the top rubber-band (the summary travels with the content); the bottom
 * rubber-band is ignored by clamping scrollTop to maxTop.
 *
 * Invariant: the scroll range must cover the summary height (GridTable's ScrollFill),
 * otherwise a fully hidden state is unreachable.
 */
export const computeSummaryOffset = (
  { scrollTop, scrollHeight, clientHeight }: SummaryScrollMetrics,
  summaryHeight: number,
): number => {
  const maxTop = Math.max(0, scrollHeight - clientHeight)
  return Math.min(Math.min(scrollTop, maxTop), summaryHeight)
}

/** scrollTop to finish a half-way gesture, or null when offset is at/beyond an edge. */
export const getSettleTarget = (offset: number, summaryHeight: number): number | null => {
  if (offset <= 0 || offset >= summaryHeight) return null
  return offset > summaryHeight / 2 ? summaryHeight : 0
}

/**
 * Visual state of the collapsing summary: the card flattens from its top edge while its
 * content fades out by mid-way, so the text never looks squashed.
 */
export const getSummaryCollapseStyle = (
  offset: number,
  summaryHeight: number,
): SummaryCollapseStyle => {
  if (summaryHeight <= 0) return { scale: 1, contentOpacity: 1, bounceShift: 0 }
  const progress = clamp(offset / summaryHeight, 0, 1)
  return {
    scale: 1 - progress,
    contentOpacity: clamp(1 - 2 * progress, 0, 1),
    bounceShift: Math.max(0, -offset),
  }
}
